import React, { useState } from 'react';
import { 
  CalendarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ChartBarIcon,
  ScaleIcon
} from '@heroicons/react/24/outline';
import { useFarm } from '../../contexts/FarmContext';
import { useAnalyticsData } from '../../hooks/useSupabaseData';
import FarmSelector from '../../components/FarmSelector';
import { analyticsData } from '../../data/mockData';

const MetricCard = ({ title, value, prefix = '', suffix = '', trend, color = 'gray' }) => {
  const colorClasses = {
    green: 'text-green-600 bg-green-50 border-green-200',
    red: 'text-red-600 bg-red-50 border-red-200',
    blue: 'text-blue-600 bg-blue-50 border-blue-200',
    yellow: 'text-yellow-600 bg-yellow-50 border-yellow-200',
    gray: 'text-gray-600 bg-gray-50 border-gray-200'
  };

  return (
    <div className={`card border-l-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">
            {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
          </p>
          {trend && (
            <div className={`flex items-center mt-1 text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? (
                <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
              ) : (
                <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
              )}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Analytics = () => {
  // State for filters
  const [dateRange, setDateRange] = useState({
    start: '2024-12-02',
    end: '2025-08-31'
  });
  const [selectedPeriod, setSelectedPeriod] = useState('9 Months');

  // Get farm context and data
  const { getActiveFarmIds, loading: farmLoading, hasFarms } = useFarm();
  const { 
    data: stats, 
    loading: dataLoading, 
    error 
  } = useAnalyticsData(getActiveFarmIds(), dateRange.start, dateRange.end);

  // Use Supabase data if available, otherwise fallback to mock
  const data = stats || analyticsData;
  const loading = farmLoading || dataLoading;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Financial Analytics</h1>
        {/* Farm Selector */}
        {hasFarms && <FarmSelector />}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Loading analytics data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading data: {error.message}</p>
        </div>
      )}

      {/* Analytics Content */}
      {!loading && (
        <>
          {/* Overview Tab */}
          <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button className="border-b-2 border-primary-500 py-2 px-1 text-sm font-medium text-primary-600">
            Overview
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
            className="input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">&nbsp;</label>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
            className="input"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="input"
          >
            <option>9 Months</option>
            <option>6 Months</option>
            <option>3 Months</option>
            <option>1 Month</option>
          </select>
        </div>
      </div>

      {/* Main Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <MetricCard
          title="Total Sales"
          value={data.totalSales}
          prefix="RM"
          color="green"
        />
        <MetricCard
          title="Total Cost"
          value={data.totalCost}
          prefix="RM"
          color="red"
        />
        <MetricCard
          title="Net Profit"
          value={data.netProfit}
          prefix="RM"
          color={data.netProfit >= 0 ? "green" : "red"}
        />
        <MetricCard
          title="Profit Margin"
          value={data.profitMargin}
          suffix="%"
          color={data.profitMargin >= 0 ? "green" : "red"}
        />
      </div>

      {/* Secondary Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Harvest, kg"
          value={data.totalHarvest}
          color="blue"
        />
        <MetricCard
          title="Total Sold, kg"
          value={data.totalSold}
          color="blue"
        />
        <MetricCard
          title="Total Orders"
          value={data.totalOrders}
          color="gray"
        />
        <MetricCard
          title="Total Development Cost"
          value={0}
          prefix="RM"
          color="yellow"
        />
        <MetricCard
          title="Total Labour Cost"
          value={0}
          prefix="RM"
          color="yellow"
        />
        <MetricCard
          title="Total Farm Input Cost"
          value={data.totalCost}
          prefix="RM"
          color="red"
        />
        <MetricCard
          title="Total Variable Cost & Other"
          value={0}
          prefix="RM"
          color="yellow"
        />
        <MetricCard
          title="Gross Profit"
          value={data.netProfit}
          prefix="RM"
          color={data.netProfit >= 0 ? "green" : "red"}
        />
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Averages */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Averages</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Avg. Sales Per Month</span>
              <span className="font-medium">RM{data.avgSalesPerMonth.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Avg. Gross Profit Per Month</span>
              <span className="font-medium text-red-600">RM-8,278.11</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Avg. Net Profit Per Month</span>
              <span className="font-medium text-red-600">RM-8,278.11</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Avg. Order Per Month</span>
              <span className="font-medium">{data.avgOrderPerMonth}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-600">Avg. Cost Per Month</span>
              <span className="font-medium">RM{data.avgCostPerMonth.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Per Unit Analytics */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Per Unit Analysis</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Avg. Order Size</span>
              <span className="font-medium">RM{data.avgOrderSize.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Avg. Order Size, kg</span>
              <span className="font-medium">886.7</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Avg. Price Per kg</span>
              <span className="font-medium">RM{data.avgPricePerKg}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Avg. Cost Per kg</span>
              <span className="font-medium">RM31.16</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-600">Avg. Margin Per kg</span>
              <span className="font-medium text-red-600">RM{data.avgMarginPerKg} -843%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Indicators */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Performance Indicators</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <CurrencyDollarIcon className="h-8 w-8 mx-auto text-green-600 mb-2" />
            <div className="text-2xl font-bold text-green-600">
              RM{data.totalSales.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Revenue Generated</div>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <BanknotesIcon className="h-8 w-8 mx-auto text-red-600 mb-2" />
            <div className="text-2xl font-bold text-red-600">
              RM{data.totalCost.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600">Total Expenses</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <ScaleIcon className="h-8 w-8 mx-auto text-blue-600 mb-2" />
            <div className="text-2xl font-bold text-blue-600">
              {data.totalHarvest.toLocaleString()} kg
            </div>
            <div className="text-sm text-gray-600">Total Harvest</div>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <ChartBarIcon className="h-8 w-8 mx-auto text-yellow-600 mb-2" />
            <div className="text-2xl font-bold text-yellow-600">
              {data.profitMargin}%
            </div>
            <div className="text-sm text-gray-600">Profit Margin</div>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default Analytics;
